import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Snackbar, Alert, Grid, Pagination, Select, MenuItem, FormControl, InputLabel, IconButton, Tooltip, Chip, Card, CardContent
} from '@mui/material';
import { Download, GetApp, Visibility, MonetizationOn, People, Event, Payment, TrendingUp } from '@mui/icons-material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, LineChart, Line } from 'recharts';
import { CSVLink } from 'react-csv';
import { states, cities } from './data/Doctorstatecity.js';

// Helper function to transform cities object into an array
const transformCitiesToArray = (citiesObj) => {
  const citiesArray = [];
  for (const state in citiesObj) {
    if (Object.prototype.hasOwnProperty.call(citiesObj, state)) {
      citiesObj[state].forEach(city => {
        citiesArray.push({ name: city, state });
      });
    }
  }
  return citiesArray;
};

const citiesArray = transformCitiesToArray(cities);

const PaymentManagement = () => {
  const { adminUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [financialStats, setFinancialStats] = useState({
    totalRevenueToday: 0,
    totalRevenueAllTime: 0,
    todaysTotalEarnings: 0,
    allTimeEarnings: 0,
    patientCommissionToday: 0,
    patientCommissionAllTime: 0,
    doctorCommissionToday: 0,
    doctorCommissionAllTime: 0,
    completedAppointmentsToday: 0,
    completedAppointmentsAllTime: 0,
    pendingPayouts: 0,
    todaysTotalPaymentToDoctors: 0,
    totalAmountPaidToDoctorsAllTime: 0,
    pendingWithdrawalRequests: 0,
    approvedWithdrawalsTodayCount: 0,
    approvedWithdrawalsToday: 0,
    approvedWithdrawalsAllTime: 0,
    pendingEarnings: 0,
    payoutsLast7Days: 0,
    approvedWithdrawalsByDate: [],
    rejectedWithdrawalsByDate: [],
  });
  const [earnings, setEarnings] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [commissionRate, setCommissionRate] = useState({ patient: 30, doctor: 10 });
  const [minWithdrawal, setMinWithdrawal] = useState(500);
  const [filters, setFilters] = useState({
    doctorName: '',
    startDate: '',
    endDate: '',
    state: '',
    city: '',
  });
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [bulkPayoutDialog, setBulkPayoutDialog] = useState(false);
  const [revenueBreakdownDialog, setRevenueBreakdownDialog] = useState(false);
  const [withdrawalFilters, setWithdrawalFilters] = useState({
    status: 'all',
    doctorName: '',
  });

  useEffect(() => {
    if (!adminUser || !adminUser.accessToken) {
      navigate('/admin-login');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      try {
        const [earningsRes, withdrawalsRes, settingsRes] = await Promise.all([
          api.get('/admin/earnings-payments', config),
          api.get('/admin/withdrawals', config),
          api.get('/admin/platform-settings', config),
        ]);

        // Update financial stats using backend data
        updateFinancialStats(earningsRes.data, withdrawalsRes.data);

        // Aggregate earnings data for display
        const doctorEarningsMap = earningsRes.data.breakdown.reduce((map, e) => {
          const key = e.doctorId;
          if (!map[key]) {
            map[key] = {
              doctorId: e.doctorId,
              doctorName: e.doctorName,
              state: e.state,
              city: e.city,
              totalPaid: 0,
              doctorFee: 0,
              patientCommission: 0,
              doctorCommission: 0,
              finalDoctorEarning: 0,
              totalWithdrawn: 0,
              pendingEarnings: 0,
              appointmentIds: [],
            };
          }
          map[key].totalPaid += e.totalPaid;
          map[key].doctorFee += e.doctorFee;
          map[key].patientCommission += e.patientCommission;
          map[key].doctorCommission += e.doctorCommission;
          map[key].finalDoctorEarning += e.finalDoctorEarning;
          map[key].appointmentIds.push(e.appointmentId);
          return map;
        }, {});

        const aggregatedEarnings = Object.values(doctorEarningsMap).map(e => {
          const doctorWithdrawals = withdrawalsRes.data.filter(w => w.doctorId === e.doctorId && w.status === 'approved');
          const totalWithdrawn = doctorWithdrawals.reduce((sum, w) => sum + w.amount, 0);
          const pendingEarnings = e.finalDoctorEarning - totalWithdrawn;
          return { ...e, totalWithdrawn, pendingEarnings };
        });

        setEarnings(aggregatedEarnings);
        setWithdrawals(withdrawalsRes.data);
        setCommissionRate({
          patient: settingsRes.data.patientCommission || 30,
          doctor: settingsRes.data.doctorCommission || 10,
        });
        setMinWithdrawal(settingsRes.data.minWithdrawal || 500);
      } catch (err) {
        console.error('Fetch error:', err.response?.data || err.message);
        setSnackbar({ open: true, message: 'Failed to fetch payment data', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [adminUser, navigate]);

  const updateFinancialStats = (earningsData, withdrawalsData) => {
    const today = new Date().toISOString().split('T')[0];
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const payoutsLast7Days = withdrawalsData
      .filter(w => w.status === 'approved' && new Date(w.dateOfPayment) >= sevenDaysAgo)
      .reduce((sum, w) => sum + w.amount, 0);

    const approvedWithdrawalsByDate = withdrawalsData
      .filter(w => w.status === 'approved' && w.dateOfPayment)
      .reduce((acc, w) => {
        const dateTime = new Date(w.dateOfPayment).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
        if (!acc.some(item => item.date === dateTime)) {
          acc.push({ date: dateTime, amount: 0, count: 0 });
        }
        const item = acc.find(item => item.date === dateTime);
        item.amount += w.amount;
        item.count += 1;
        return acc;
      }, []);

    const rejectedWithdrawalsByDate = withdrawalsData
      .filter(w => w.status === 'rejected' && w.dateOfRejection)
      .reduce((acc, w) => {
        const dateTime = new Date(w.dateOfRejection).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
        if (!acc.some(item => item.date === dateTime)) {
          acc.push({ date: dateTime, amount: 0, count: 0 });
        }
        const item = acc.find(item => item.date === dateTime);
        item.amount += w.amount;
        item.count += 1;
        return acc;
      }, []);

    setFinancialStats({
      // Use pre-calculated values from backend
      totalRevenueToday: earningsData.totalRevenueToday || 0,
      totalRevenueAllTime: earningsData.totalRevenueAllTime || 0,
      todaysTotalEarnings: earningsData.totalPlatformEarningsToday || 0,
      allTimeEarnings: earningsData.totalPlatformEarningsAllTime || 0,
      patientCommissionToday: earningsData.patientCommissionToday || 0,
      patientCommissionAllTime: earningsData.patientCommissionAllTime || 0,
      doctorCommissionToday: earningsData.doctorCommissionToday || 0,
      doctorCommissionAllTime: earningsData.doctorCommissionAllTime || 0,
      completedAppointmentsToday: earningsData.completedAppointmentsToday || 0,
      completedAppointmentsAllTime: earningsData.completedAppointmentsAllTime || 0,

      // Withdrawal-related stats
      pendingPayouts: withdrawalsData.filter(w => w.status === 'pending').reduce((sum, w) => sum + w.amount, 0),
      todaysTotalPaymentToDoctors: withdrawalsData.filter(w => w.status === 'approved' && w.dateOfPayment?.split('T')[0] === today).reduce((sum, w) => sum + w.amount, 0),
      totalAmountPaidToDoctorsAllTime: withdrawalsData.filter(w => w.status === 'approved').reduce((sum, w) => sum + w.amount, 0),
      pendingWithdrawalRequests: withdrawalsData.filter(w => w.status === 'pending').length,
      approvedWithdrawalsTodayCount: withdrawalsData.filter(w => w.status === 'approved' && w.dateOfPayment?.split('T')[0] === today).length,
      approvedWithdrawalsToday: withdrawalsData.filter(w => w.status === 'approved' && w.dateOfPayment?.split('T')[0] === today).reduce((sum, w) => sum + w.amount, 0),
      approvedWithdrawalsAllTime: withdrawalsData.filter(w => w.status === 'approved').reduce((sum, w) => sum + w.amount, 0),
      pendingEarnings: earningsData.breakdown.reduce((sum, e) => sum + e.finalDoctorEarning, 0) - withdrawalsData.filter(w => w.status === 'approved').reduce((sum, w) => sum + w.amount, 0),
      payoutsLast7Days,
      approvedWithdrawalsByDate,
      rejectedWithdrawalsByDate,
    });
  };

  // Rest of the code remains unchanged, including the UI rendering, filters, charts, etc.

  const filteredEarnings = earnings.filter(e => {
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;

    return (
      (!filters.doctorName || e.doctorName.toLowerCase().includes(filters.doctorName.toLowerCase())) &&
      (!startDate || new Date(e.date) >= startDate) &&
      (!endDate || new Date(e.date) <= endDate) &&
      (!filters.state || e.state?.toLowerCase() === filters.state.toLowerCase()) &&
      (!filters.city || e.city?.toLowerCase() === filters.city.toLowerCase())
    );
  });

  const paginatedEarnings = filteredEarnings.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const csvData = filteredEarnings.map(e => ({
    'Doctor Name': e.doctorName,
    State: e.state || 'N/A',
    City: e.city || 'N/A',
    'Total Paid': e.totalPaid,
    'Doctor Fee': e.doctorFee,
    'Patient Commission': e.patientCommission,
    'Doctor Commission': e.doctorCommission,
    'Final Doctor Earning': e.finalDoctorEarning,
    'Total Withdrawn': e.totalWithdrawn,
    'Pending Earnings': e.pendingEarnings,
  }));

  const commissionPieData = [
    { name: 'Patient Commission', value: financialStats.patientCommissionAllTime },
    { name: 'Doctor Commission', value: financialStats.doctorCommissionAllTime },
    { name: 'Net Earnings', value: financialStats.totalRevenueAllTime - financialStats.patientCommissionAllTime - financialStats.doctorCommissionAllTime },
  ];
  const COLORS = ['#0088FE', '#FFBB28', '#00C49F'];

  const earningsTrendData = (period) => {
    const days = period === 'daily' ? 7 : period === 'weekly' ? 30 : period === 'monthly' ? 90 : 365;
    return Array.from({ length: days }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayEarnings = earnings.reduce((sum, e) => sum + (e.date?.split('T')[0] === date.toISOString().split('T')[0] ? e.totalPaid : 0), 0);
      return {
        name: date.toLocaleDateString('en-US', { weekday: 'short' }),
        Revenue: dayEarnings,
      };
    }).reverse();
  };

  const topDoctors = earnings.sort((a, b) => b.finalDoctorEarning - a.finalDoctorEarning).slice(0, 5).map(e => ({ name: e.doctorName, earnings: e.finalDoctorEarning }));
  const topCities = [...earnings.reduce((map, e) => {
    const key = `${e.city || 'Unknown'}, ${e.state || 'Unknown'}`;
    map.set(key, (map.get(key) || 0) + e.totalPaid);
    return map;
  }, new Map())].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, revenue]) => ({ name, revenue }));

  const revenueBreakdownByStateCity = earnings.reduce((map, e) => {
    const key = `${e.state || 'Unknown'}, ${e.city || 'Unknown'}`;
    if (!map[key]) {
      map[key] = { state: e.state, city: e.city, revenue: 0, appointments: 0 };
    }
    map[key].revenue += e.totalPaid;
    map[key].appointments += 1;
    return map;
  }, {});

  const handleBulkPayout = async () => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending');
      const pendingIds = pendingWithdrawals.map(w => w._id);
      const today = new Date().toISOString();
      await api.post('/admin/withdrawals/bulk-approve', { withdrawalIds: pendingIds, dateOfPayment: today }, config);
      const updatedWithdrawals = withdrawals.map(w => 
        pendingIds.includes(w._id) ? { ...w, status: 'approved', dateOfPayment: today } : w
      );
      setWithdrawals(updatedWithdrawals);

      const totalPendingAmount = pendingWithdrawals.reduce((sum, w) => sum + w.amount, 0);
      const pendingCount = pendingWithdrawals.length;
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const newPayoutsLast7Days = updatedWithdrawals
        .filter(w => w.status === 'approved' && new Date(w.dateOfPayment) >= sevenDaysAgo)
        .reduce((sum, w) => sum + w.amount, 0);

      setFinancialStats(prev => ({
        ...prev,
        pendingPayouts: 0,
        todaysTotalPaymentToDoctors: prev.todaysTotalPaymentToDoctors + totalPendingAmount,
        totalAmountPaidToDoctorsAllTime: prev.totalAmountPaidToDoctorsAllTime + totalPendingAmount,
        pendingWithdrawalRequests: 0,
        approvedWithdrawalsTodayCount: prev.approvedWithdrawalsTodayCount + pendingCount,
        approvedWithdrawalsToday: prev.approvedWithdrawalsToday + totalPendingAmount,
        approvedWithdrawalsAllTime: prev.approvedWithdrawalsAllTime + totalPendingAmount,
        pendingEarnings: prev.pendingEarnings - totalPendingAmount,
        todaysTotalEarnings: prev.todaysTotalEarnings,
        payoutsLast7Days: newPayoutsLast7Days,
      }));

      setBulkPayoutDialog(false);
      setSnackbar({ open: true, message: 'Bulk payout processed', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to process bulk payout', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async () => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      await api.put('/admin/platform-settings', { patientCommission: commissionRate.patient, doctorCommission: commissionRate.doctor, minWithdrawal }, config);
      setSnackbar({ open: true, message: 'Settings updated', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update settings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const today = new Date().toISOString().split('T')[0];

  const filteredWithdrawals = withdrawals.filter(w => {
    const matchesStatus = withdrawalFilters.status === 'all' || w.status === withdrawalFilters.status;
    const matchesDoctorName = !withdrawalFilters.doctorName || w.doctorName.toLowerCase().includes(withdrawalFilters.doctorName.toLowerCase());
    const isToday = w.requestDate.split('T')[0] === today;
    return matchesStatus && matchesDoctorName && isToday;
  });

  if (loading) return <CircularProgress />;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Payment Management</Typography>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Financial Overview</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Revenue Overview */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <MonetizationOn sx={{ mr: 1, color: '#0088FE' }} /> Revenue Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Total Revenue Today</Typography>
                    <Typography variant="h6">₹{financialStats.totalRevenueToday.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">All-Time Revenue</Typography>
                    <Typography variant="h6">₹{financialStats.totalRevenueAllTime.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Earnings Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <TrendingUp sx={{ mr: 1, color: '#00C49F' }} /> Earnings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Today&#39;s Earnings</Typography>
                    <Typography variant="h6">₹{financialStats.todaysTotalEarnings.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">All-Time Earnings</Typography>
                    <Typography variant="h6">₹{financialStats.allTimeEarnings.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Commission Breakdown */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <People sx={{ mr: 1, color: '#FFBB28' }} /> Commission Breakdown
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Patients (Today)</Typography>
                    <Typography variant="h6">₹{financialStats.patientCommissionToday.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Patients (All Time)</Typography>
                    <Typography variant="h6">₹{financialStats.patientCommissionAllTime.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Doctors (Today)</Typography>
                    <Typography variant="h6">₹{financialStats.doctorCommissionToday.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Doctors (All Time)</Typography>
                    <Typography variant="h6">₹{financialStats.doctorCommissionAllTime.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Appointment Summary */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Event sx={{ mr: 1, color: '#00C49F' }} /> Appointment Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Tooltip title="Appointments completed today based on completion date">
                      <Typography variant="body2" color="textSecondary">Completed Today</Typography>
                    </Tooltip>
                    <Typography variant="h6">{financialStats.completedAppointmentsToday}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">All Time</Typography>
                    <Typography variant="h6">{financialStats.completedAppointmentsAllTime}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Doctor Payouts & Withdrawals */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Payment sx={{ mr: 1, color: '#8884d8' }} /> Doctor Payouts & Withdrawals
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Pending Payouts</Typography>
                    <Typography variant="h6">₹{financialStats.pendingPayouts.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Pending Earnings</Typography>
                    <Typography variant="h6">₹{financialStats.pendingEarnings.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Total Paid (All Time)</Typography>
                    <Typography variant="h6">₹{financialStats.totalAmountPaidToDoctorsAllTime.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Pending Requests</Typography>
                    <Typography variant="h6">{financialStats.pendingWithdrawalRequests}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Approved Today (Count)</Typography>
                    <Typography variant="h6">{financialStats.approvedWithdrawalsTodayCount}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Approved Today (Amount)</Typography>
                    <Typography variant="h6">₹{financialStats.approvedWithdrawalsToday.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Approved (All Time)</Typography>
                    <Typography variant="h6">₹{financialStats.approvedWithdrawalsAllTime.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Earnings & Payout Insights */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <TrendingUp sx={{ mr: 1, color: '#82ca9d' }} /> Earnings & Payout Insights
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Today&#39;s Payment to Doctors</Typography>
                    <Typography variant="h6">₹{financialStats.todaysTotalPaymentToDoctors.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Payouts (Last 7 Days)</Typography>
                    <Typography variant="h6">₹{financialStats.payoutsLast7Days.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Revenue Breakdown</Typography>
                    <Button variant="outlined" size="small" onClick={() => setRevenueBreakdownDialog(true)}>View</Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Approved Withdrawals by Date */}
        <Typography variant="h6" gutterBottom>Approved Withdrawals by Date</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date & Time</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {financialStats.approvedWithdrawalsByDate.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item.date}</TableCell>
                <TableCell>₹{item.amount.toFixed(2)}</TableCell>
                <TableCell>{item.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Rejected Withdrawals by Date */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Rejected Withdrawals by Date</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date & Time</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {financialStats.rejectedWithdrawalsByDate.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item.date}</TableCell>
                <TableCell>₹{item.amount.toFixed(2)}</TableCell>
                <TableCell>{item.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Graphs & Analytics */}
        <Typography variant="h6" gutterBottom>Graphs & Analytics</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1">Earnings Trend</Typography>
            <LineChart width={300} height={200} data={earningsTrendData('daily')}>
              <XAxis dataKey="name" />
              <YAxis />
              <RechartsTooltip />
              <Line type="monotone" dataKey="Revenue" stroke="#8884d8" />
            </LineChart>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1">Top Earning Doctors</Typography>
            <BarChart width={300} height={200} data={topDoctors}>
              <XAxis dataKey="name" />
              <YAxis />
              <RechartsTooltip />
              <Bar dataKey="earnings" fill="#82ca9d" />
            </BarChart>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1">Most Active Cities/States</Typography>
            <BarChart width={300} height={200} data={topCities}>
              <XAxis dataKey="name" />
              <YAxis />
              <RechartsTooltip />
              <Bar dataKey="revenue" fill="#ffc107" />
            </BarChart>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1">Earnings Distribution</Typography>
            <PieChart width={300} height={200}>
              <Pie data={commissionPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} fill="#8884d8">
                {commissionPieData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
              </Pie>
              <RechartsTooltip />
              <Legend />
            </PieChart>
          </Grid>
        </Grid>

        {/* Payment Filters & Search */}
        <Typography variant="h6" gutterBottom>Doctors&#39; Earnings Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <TextField label="Doctor Name" value={filters.doctorName} onChange={e => setFilters({ ...filters, doctorName: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField type="date" label="Start Date" value={filters.startDate} onChange={e => setFilters({ ...filters, startDate: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField type="date" label="End Date" value={filters.endDate} onChange={e => setFilters({ ...filters, endDate: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>State</InputLabel>
              <Select value={filters.state} onChange={e => setFilters({ ...filters, state: e.target.value })}>
                <MenuItem value="">All</MenuItem>
                {states.map(state => (
                  <MenuItem key={state} value={state}>{state}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>City</InputLabel>
              <Select value={filters.city} onChange={e => setFilters({ ...filters, city: e.target.value })}>
                <MenuItem value="">All</MenuItem>
                {citiesArray
                  .filter(city => !filters.state || city.state === filters.state)
                  .map(city => (
                    <MenuItem key={city.name} value={city.name}>{city.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button variant="contained" fullWidth>
              <CSVLink data={csvData} filename="doctor_earnings.csv" style={{ textDecoration: 'none', color: 'white' }}>
                Export CSV
              </CSVLink>
            </Button>
          </Grid>
        </Grid>

        {/* Doctors' Earnings Summary Table */}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Doctor Name</TableCell>
              <TableCell>State</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Total Paid</TableCell>
              <TableCell>Doctor Fee</TableCell>
              <TableCell>Patient Commission</TableCell>
              <TableCell>Doctor Commission</TableCell>
              <TableCell>Final Doctor Earning</TableCell>
              <TableCell>Total Withdrawn</TableCell>
              <TableCell>Pending Earnings</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedEarnings.map(e => (
              <TableRow key={e.doctorId}>
                <TableCell>{e.doctorName}</TableCell>
                <TableCell>{e.state || 'N/A'}</TableCell>
                <TableCell>{e.city || 'N/A'}</TableCell>
                <TableCell>₹{e.totalPaid.toFixed(2)}</TableCell>
                <TableCell>₹{e.doctorFee.toFixed(2)}</TableCell>
                <TableCell>₹{e.patientCommission.toFixed(2)}</TableCell>
                <TableCell>₹{e.doctorCommission.toFixed(2)}</TableCell>
                <TableCell>₹{e.finalDoctorEarning.toFixed(2)}</TableCell>
                <TableCell>₹{e.totalWithdrawn.toFixed(2)}</TableCell>
                <TableCell>₹{e.pendingEarnings.toFixed(2)}</TableCell>
                <TableCell>
                  <Tooltip title="View Payment Details">
                    <IconButton onClick={() => navigate(`/admin/doctor-payment-details/${e.doctorId}`)}>
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination count={Math.ceil(filteredEarnings.length / rowsPerPage)} page={page} onChange={(e, p) => setPage(p)} sx={{ mt: 2 }} />

        {/* Pending Amount to be Paid by Admin */}
        <Typography variant="h6" sx={{ mt: 3 }}>Pending Amount to be Paid by Admin</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Doctor Name</TableCell>
              <TableCell>State</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Pending Earnings</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEarnings.filter(e => e.pendingEarnings > 0).map(e => (
              <TableRow key={e.doctorId}>
                <TableCell>{e.doctorName}</TableCell>
                <TableCell>{e.state || 'N/A'}</TableCell>
                <TableCell>{e.city || 'N/A'}</TableCell>
                <TableCell>₹{e.pendingEarnings.toFixed(2)}</TableCell>
                <TableCell>
                  <Tooltip title="View Payment Details">
                    <IconButton onClick={() => navigate(`/admin/doctor-payment-details/${e.doctorId}`)}>
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Withdrawal Requests */}
        <Typography variant="h6" sx={{ mt: 3 }}>Withdrawal Requests (Today)</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={withdrawalFilters.status} onChange={e => setWithdrawalFilters({ ...withdrawalFilters, status: e.target.value })}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Doctor Name" value={withdrawalFilters.doctorName} onChange={e => setWithdrawalFilters({ ...withdrawalFilters, doctorName: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button variant="contained" onClick={() => setBulkPayoutDialog(true)} fullWidth>Process Bulk Payout</Button>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button variant="outlined" onClick={() => navigate('/admin/withdrawal-requests-history')} fullWidth>View Withdrawal Requests History</Button>
          </Grid>
        </Grid>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Request Date & Time</TableCell>
              <TableCell>Doctor Name</TableCell>
              <TableCell>State</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Amount Requested</TableCell>
              <TableCell>Available Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Date & Time of Payment/Rejection</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredWithdrawals.length > 0 ? (
              filteredWithdrawals.map(w => {
                const doctorEarnings = earnings.find(e => e.doctorId === w.doctorId)?.finalDoctorEarning || 0;
                const withdrawnAmount = withdrawals.filter(wd => wd.doctorId === w.doctorId && wd.status === 'approved').reduce((sum, wd) => sum + wd.amount, 0);
                const availableBalance = doctorEarnings - withdrawnAmount;

                return (
                  <TableRow key={w._id}>
                    <TableCell>{new Date(w.requestDate).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}</TableCell>
                    <TableCell>{w.doctorName}</TableCell>
                    <TableCell>{w.state || 'N/A'}</TableCell>
                    <TableCell>{w.city || 'N/A'}</TableCell>
                    <TableCell>₹{w.amount.toFixed(2)}</TableCell>
                    <TableCell>₹{availableBalance.toFixed(2)}</TableCell>
                    <TableCell><Chip label={w.status} color={w.status === 'approved' ? 'success' : w.status === 'rejected' ? 'error' : 'warning'} /></TableCell>
                    <TableCell>{w.method}</TableCell>
                    <TableCell>
                      {w.status === 'approved' && w.dateOfPayment
                        ? new Date(w.dateOfPayment).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })
                        : w.status === 'rejected' && w.dateOfRejection
                        ? new Date(w.dateOfRejection).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Payment Details">
                        <IconButton onClick={() => navigate(`/admin/doctor-payment-details/${w.doctorId._id || w.doctorId}`)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {w.status === 'approved' && (
                        <Tooltip title="Download Invoice">
                          <IconButton onClick={() => console.log(`Download invoice for ${w._id}`)}>
                            <Download />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">No withdrawal requests for today</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Commission & Payment Settings */}
        <Typography variant="h6" sx={{ mt: 3 }}>Payment Settings</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <TextField label="Patient Commission (%)" value={commissionRate.patient} onChange={e => setCommissionRate({ ...commissionRate, patient: e.target.value })} fullWidth type="number" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Doctor Commission (%)" value={commissionRate.doctor} onChange={e => setCommissionRate({ ...commissionRate, doctor: e.target.value })} fullWidth type="number" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Minimum Withdrawal (₹)" value={minWithdrawal} onChange={e => setMinWithdrawal(e.target.value)} fullWidth type="number" />
          </Grid>
          <Grid item xs={12}>
            <Button onClick={handleUpdateSettings} disabled={loading}>Save Settings</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Payout Dialog */}
      <Dialog open={bulkPayoutDialog} onClose={() => setBulkPayoutDialog(false)}>
        <DialogTitle>Process Bulk Payout</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to approve all pending payouts?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkPayoutDialog(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleBulkPayout} color="success" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revenue Breakdown Dialog */}
      <Dialog open={revenueBreakdownDialog} onClose={() => setRevenueBreakdownDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>Revenue Breakdown by State/City</DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>State</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Revenue</TableCell>
                <TableCell>Appointments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.values(revenueBreakdownByStateCity).map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>{entry.state || 'N/A'}</TableCell>
                  <TableCell>{entry.city || 'N/A'}</TableCell>
                  <TableCell>₹{entry.revenue.toFixed(2)}</TableCell>
                  <TableCell>{entry.appointments}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevenueBreakdownDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentManagement;